import { useEffect, useState, useRef, createContext, useContext } from "react";
import "@/App.css";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useLocation,
  useNavigate,
} from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";

// Pages
import LandingPage from "./pages/LandingPage";
import LoginPage from "./pages/LoginPage";
import SignupPage from "./pages/SignupPage";
import ProfilePage from "./pages/ProfilePage";
import AuthCallback from "./pages/AuthCallback";
import AgentDashboard from "./pages/dashboards/AgentDashboard";
import AgentPropertiesPage from "./pages/dashboards/AgentPropertiesPage";
import AgentDraftsPage from "./pages/dashboards/AgentDraftsPage";
import SellerDashboard from "./pages/dashboards/SellerDashboard";
import WholesalerDashboard from "./pages/dashboards/WholesalerDashboard";
import InvestorDashboard from "./pages/dashboards/InvestorDashboard";
import InvestorLiveDealsPage from "./pages/dashboards/InvestorLiveDealsPage";
import InvestorMyDealsPage from "./pages/dashboards/InvestorMyDealsPage";
import WholesalerLiveDealsPage from "./pages/dashboards/WholesalerLiveDealsPage";
import WholesalerMyDealsPage from "./pages/dashboards/WholesalerMyDealsPage";
import AdminDashboard from "./pages/dashboards/AdminDashboard";
import RoleSelectionPage from "./pages/RoleSelectionPage";

// Admin Pages
import LiveDealsPage from "./pages/admin/LiveDealsPage";
import InvestorManagementPage from "./pages/admin/InvestorManagementPage";
import DocumentOversightPage from "./pages/admin/DocumentOversightPage";
import UploadPlatformNDAPage from "./pages/admin/UploadPlatformNDAPage";

// Modals
import NDASignModal from "./components/NDASignModal";

// Theme Provider
import { ThemeProvider } from "./context/ThemeContext";
import { AlertProvider } from "./context/AlertContext";

const BPOHOMES_URL =
  process.env.REACT_APP_BPOHOMES_URL ?? "https://testing.bpotech.io";
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const API = `${BACKEND_URL}/api`;
const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

// Configure axios to include auth token from localStorage
const getAuthHeaders = () => {
  const token = localStorage.getItem("auth_token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// Detected once at module load — stable for the lifetime of the page
const isSSOFlow = new URLSearchParams(window.location.search).has(
  "access_token",
);

// Auth Provider Component
const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  // Keep loading=true during SSO flow so ProtectedRoute never fires early
  const [loading, setLoading] = useState(true);

  const checkAuth = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, {
        withCredentials: true,
        headers: getAuthHeaders(),
      });
      setUser(response.data);
    } catch (error) {
      // Clear invalid token
      localStorage.removeItem("auth_token");
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    // SSO flow — BpoHomesHandler owns auth init, keep loading=true until it finishes
    if (isSSOFlow) return;
    checkAuth();
  }, []);

  const login = async (email, password) => {
    try {
      const response = await axios.post(
        `${API}/auth/login`,
        { email, password },
        { withCredentials: true },
      );
      // Store token in localStorage for session persistence
      if (response.data.access_token) {
        localStorage.setItem("auth_token", response.data.access_token);
      }
      setUser(response.data.user);
      toast.success("Login successful!");
      return response.data;
    } catch (error) {
      const message = error.response?.data?.detail || "Login failed";
      toast.error(message);
      throw error;
    }
  };

  const signup = async (name, email, password, role, state) => {
    try {
      const response = await axios.post(
        `${API}/auth/signup`,
        { name, email, password, role, state },
        { withCredentials: true },
      );
      // Store token in localStorage for session persistence
      if (response.data.access_token) {
        localStorage.setItem("auth_token", response.data.access_token);
      }
      setUser(response.data.user);
      toast.success("Account created successfully!");
      return response.data;
    } catch (error) {
      const message = error.response?.data?.detail || "Signup failed";
      toast.error(message);
      throw error;
    }
  };

  const logout = async () => {
    const isBPOUser = user?.auth_provider === "bpohomes";
    try {
      await axios.post(
        `${API}/auth/logout`,
        {},
        {
          withCredentials: true,
          headers: getAuthHeaders(),
        },
      );
    } catch (error) {
      console.error("Logout error:", error);
    } finally {
      localStorage.removeItem("auth_token");
      localStorage.removeItem("privacy_statement_url_cache");
      setUser(null);
      if (isBPOUser) {
        window.location.href = BPOHOMES_URL;
      } else {
        toast.success("Logged out successfully");
      }
    }
  };

  const updateRole = async (role) => {
    try {
      const response = await axios.put(
        `${API}/auth/role?role=${role}`,
        {},
        { withCredentials: true, headers: getAuthHeaders() },
      );
      setUser(response.data);
      toast.success("Role updated successfully!");
      return response.data;
    } catch (error) {
      toast.error("Something went wrong. Please try again.");
      throw error;
    }
  };

  const bpoHomesLogin = async (accessToken) => {
    try {
      const response = await axios.post(
        `${API}/auth/verify-bpohomes-user`,
        { token: accessToken },
        { withCredentials: true },
      );
      const { token } = response.data;
      if (!token) throw new Error("Login failed");
      localStorage.setItem("auth_token", token);
      const meResponse = await axios.get(`${API}/auth/me`, {
        withCredentials: true,
        headers: { Authorization: `Bearer ${token}` },
      });
      setUser(meResponse.data);
      return meResponse.data;
    } finally {
      // Always release the loading gate so the app doesn't hang
      setLoading(false);
    }
  };

  const processGoogleSession = async (sessionId) => {
    try {
      const response = await axios.post(
        `${API}/auth/session`,
        { session_id: sessionId },
        { withCredentials: true },
      );
      // Store token in localStorage for session persistence
      if (response.data.access_token) {
        localStorage.setItem("auth_token", response.data.access_token);
      }
      setUser(response.data.user);
      return response.data;
    } catch (error) {
      const message = error.response?.data?.detail || "Google login failed";
      toast.error(message);
      throw error;
    }
  };

  const processGoogleToken = async (token) => {
    try {
      localStorage.setItem("auth_token", token);
      const response = await axios.get(`${API}/auth/me`, {
        withCredentials: true,
        headers: { Authorization: `Bearer ${token}` },
      });
      setUser(response.data);
      return { access_token: token, user: response.data };
    } catch (error) {
      localStorage.removeItem("auth_token");
      const message = error.response?.data?.detail || "Google login failed";
      toast.error(message);
      throw error;
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        login,
        signup,
        logout,
        updateRole,
        processGoogleSession,
        processGoogleToken,
        bpoHomesLogin,
        checkAuth,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

// Protected Route Component
const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="loading-spinner border-blue-500"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    return <Navigate to={getDashboardRoute(user.role)} replace />;
  }

  // Force incomplete investors to profile before accessing any investor page
  if (
    isInvestorProfileIncomplete(user) &&
    location.pathname !== "/dashboard/profile"
  ) {
    return <Navigate to="/dashboard/profile" replace />;
  }

  return children;
};

// Get dashboard route based on role
export const getDashboardRoute = (role) => {
  switch (role) {
    case "agent":
      return "/dashboard/agent";
    case "seller":
      return "/dashboard/seller";
    case "wholesaler":
      return "/dashboard/wholesaler";
    case "investor":
      return "/dashboard/investor";
    case "admin":
      return "/dashboard/admin";
    default:
      return "/select-role";
  }
};

// Check if investor profile is incomplete
const isInvestorProfileIncomplete = (user) => {
  if (user?.role !== "investor") return false;
  return (
    !user.available_capital ||
    !user.primary_investment_focus?.length ||
    !user.areas_of_focus_state ||
    (user.years_experience == null && user.years_experience !== 0)
  );
};

// Dashboard Redirect Component
const DashboardRedirect = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user) {
      if (user.needs_role_selection) {
        navigate("/select-role", { replace: true });
      } else if (isInvestorProfileIncomplete(user)) {
        navigate("/dashboard/profile", { replace: true });
      } else {
        navigate(getDashboardRoute(user.role), { replace: true });
      }
    }
  }, [user, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="loading-spinner border-blue-500"></div>
    </div>
  );
};

// BPOHomes SSO handler
const BpoHomesHandler = ({ token, onDone }) => {
  const { bpoHomesLogin } = useAuth();
  const navigate = useNavigate();
  const hasRun = useRef(false);

  useEffect(() => {
    if (hasRun.current) return;
    hasRun.current = true;

    const url = new URL(window.location.href);
    url.searchParams.delete("access_token");
    window.history.replaceState({}, "", url.toString());

    bpoHomesLogin(token)
      .then((user) => {
        if (!user.role || user.needs_role_selection) {
          navigate("/select-role", { replace: true });
        } else {
          navigate(getDashboardRoute(user.role), { replace: true });
        }
      })
      .catch(() => {
        toast.error("Login failed. Please try again.");
        navigate("/login", { replace: true });
      })
      .finally(onDone);
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="text-center">
        <div className="loading-spinner border-blue-500 mx-auto mb-4"></div>
        <p className="text-muted-foreground">Signing you in...</p>
      </div>
    </div>
  );
};

// App Router
const AppRouter = () => {
  const location = useLocation();
  const { user } = useAuth();
  const [bpoToken, setBpoToken] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get("access_token") || null;
  });
  const [showNDAModal, setShowNDAModal] = useState(false);

  // Only show NDA modal on dashboard routes (not landing, login, signup etc)
  const isDashboardRoute = location.pathname.startsWith("/dashboard");

  // Check if NDA needs to be signed after user is authenticated (skip for admin and investor)
  // Commented out per request: do not force sellers/wholesalers to sign the platform NDA on first login.
  /*
  useEffect(() => {
    if (
      user &&
      !user.nda_signed?.signed &&
      !["admin", "investor"].includes(user.role)
    ) {
      setShowNDAModal(true);
    } else {
      setShowNDAModal(false);
    }
  }, [user]);
  */

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }

  if (location.search.includes("code=") && location.search.includes("state=")) {
    return <AuthCallback />;
  }

  if (bpoToken) {
    return (
      <BpoHomesHandler token={bpoToken} onDone={() => setBpoToken(null)} />
    );
  }

  return (
    <>
      <Routes>
        {/* Public Routes */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/signup" element={<SignupPage />} />
        <Route path="/auth/callback" element={<AuthCallback />} />

        {/* Role Selection (for Google OAuth users) */}
        <Route
          path="/select-role"
          element={
            <ProtectedRoute>
              <RoleSelectionPage />
            </ProtectedRoute>
          }
        />

        {/* Dashboard Routes */}
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <DashboardRedirect />
            </ProtectedRoute>
          }
        />

        <Route
          path="/dashboard/agent"
          element={
            <ProtectedRoute allowedRoles={["agent"]}>
              <AgentDashboard />
            </ProtectedRoute>
          }
        />

        <Route
          path="/dashboard/agent/properties"
          element={
            <ProtectedRoute allowedRoles={["agent"]}>
              <AgentPropertiesPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/dashboard/agent/drafts"
          element={
            <ProtectedRoute allowedRoles={["agent"]}>
              <AgentDraftsPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/dashboard/seller"
          element={
            <ProtectedRoute allowedRoles={["seller"]}>
              <SellerDashboard />
            </ProtectedRoute>
          }
        />

        <Route
          path="/dashboard/seller/properties"
          element={
            <ProtectedRoute allowedRoles={["seller"]}>
              <AgentPropertiesPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/dashboard/seller/drafts"
          element={
            <ProtectedRoute allowedRoles={["seller"]}>
              <AgentDraftsPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/dashboard/wholesaler"
          element={
            <ProtectedRoute allowedRoles={["wholesaler"]}>
              <WholesalerDashboard />
            </ProtectedRoute>
          }
        />

        <Route
          path="/dashboard/wholesaler/deals"
          element={
            <ProtectedRoute allowedRoles={["wholesaler"]}>
              <WholesalerLiveDealsPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/dashboard/wholesaler/my-deals"
          element={
            <ProtectedRoute allowedRoles={["wholesaler"]}>
              <WholesalerMyDealsPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/dashboard/investor"
          element={
            <ProtectedRoute allowedRoles={["investor"]}>
              <InvestorDashboard />
            </ProtectedRoute>
          }
        />

        <Route
          path="/dashboard/investor/deals"
          element={
            <ProtectedRoute allowedRoles={["investor"]}>
              <InvestorLiveDealsPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/dashboard/investor/my-deals"
          element={
            <ProtectedRoute allowedRoles={["investor"]}>
              <InvestorMyDealsPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/dashboard/admin"
          element={
            <ProtectedRoute allowedRoles={["admin"]}>
              <AdminDashboard />
            </ProtectedRoute>
          }
        />

        {/* Profile (any authenticated user) */}
        <Route
          path="/dashboard/profile"
          element={
            <ProtectedRoute>
              <ProfilePage />
            </ProtectedRoute>
          }
        />

        {/* Admin Sub-Pages */}
        <Route
          path="/dashboard/admin/deals"
          element={
            <ProtectedRoute allowedRoles={["admin"]}>
              <LiveDealsPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/dashboard/admin/live-deals"
          element={
            <ProtectedRoute allowedRoles={["admin"]}>
              <LiveDealsPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/dashboard/admin/users"
          element={
            <ProtectedRoute allowedRoles={["admin"]}>
              <InvestorManagementPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/dashboard/admin/investors"
          element={
            <ProtectedRoute allowedRoles={["admin"]}>
              <InvestorManagementPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/dashboard/admin/documents"
          element={
            <ProtectedRoute allowedRoles={["admin"]}>
              <DocumentOversightPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/dashboard/admin/platform-settings"
          element={
            <ProtectedRoute allowedRoles={["admin"]}>
              <UploadPlatformNDAPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/dashboard/admin/upload-nda"
          element={
            <ProtectedRoute allowedRoles={["admin"]}>
              <UploadPlatformNDAPage />
            </ProtectedRoute>
          }
        />

        {/* Landing page */}
        <Route path="/" element={<LandingPage />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>

      {/* Platform NDA Modal - shown when user hasn't signed NDA */}
      {/* Commented out: do not force sellers/wholesalers to sign on first login. */}
      {false && (
        <NDASignModal
          open={showNDAModal}
          onOpenChange={setShowNDAModal}
          onSignComplete={() => setShowNDAModal(false)}
          mode="platform"
        />
      )}
    </>
  );
};

function App() {
  return (
    <ThemeProvider>
      <BrowserRouter>
        <AuthProvider>
          <AlertProvider>
            <AppRouter />
            <Toaster richColors position="top-right" />
          </AlertProvider>
        </AuthProvider>
      </BrowserRouter>
    </ThemeProvider>
  );
}

export default App;
